import { Button, Stack, Typography, Box } from '@mui/material';
import DataConfirmation from 'components/shared/data_confirmation';
import { PageTitle } from 'components/shared/page_title';
import React from 'react';
import AlertModal from 'components/shared/alert_modal';
import { ROUTES } from 'routes/constants';
import { useNavigate, useLocation } from 'react-router-dom';
import DocumentComponent from 'components/shared/document_component';

type Step1AnswersType = {
  beneficiaries: string;
  ataUuid: string | null;
  parceria: string;
  beneficiariosParceria: string;
  docParceriaUuid: string | null;
  relatorioUuid: string | null;
  instituicoes: string[];
  outraInstituicao: string;
  relatorioUuid_empaer: string | null;
  relatorioUuid_ensino: string | null;
  relatorioUuid_ongs: string | null;
  relatorioUuid_privadas: string | null;
};

interface Step2Props {
  onNext: () => void;
  onBack: () => void;
  step1Data: Step1AnswersType;
}

const INSTITUICOES_PARCEIRAS = [
  { label: 'EMPAER MT', value: 'empaer' },
  { label: 'Instituições de Ensino', value: 'ensino' },
  { label: 'Organizações – ONGs', value: 'ongs' },
  { label: 'Instituições privadas/outros', value: 'privadas' },
];

const Step2: React.FC<Step2Props> = ({ onNext, onBack, step1Data }) => {
  const [successOpen, setSuccessOpen] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Extrai o ano da rota, ex: /service_management/cifa-2027 => 2027
  const match = /cifa-(\d{4})/.exec(location.pathname);
  const anoReferencia = match ? Number(match[1]) : 2026;

  const handleConfirm = () => {
    setSuccessOpen(true);
  };

  const handleCloseSuccess = () => {
    setSuccessOpen(false);
    navigate(ROUTES[`INCLUDE_CIFA_${anoReferencia}` as keyof typeof ROUTES] as string);
  };

  return (
    <>
      <PageTitle
        title='Confirmação dos dados'
        subtitle='Confira todas as informações que foram preenchidas, e altere-as caso necessário.'
      />

      <Stack marginX='4rem' spacing='2rem'>
        <DataConfirmation
          title='Dados Gerais'
          data={[
            {
              label: 'Beneficiários atendidos exclusivamente pelo município',
              value: step1Data.beneficiaries,
            },
            {
              label: 'Ata do CMDRS',
              value: step1Data.ataUuid ? 'Documento anexado' : 'Não anexado',
            },
            {
              label: 'Parceria com instituição executora de ATER',
              value: step1Data.parceria === 'Sim' ? 'Sim' : 'Não',
            },
          ]}
        />
        {step1Data.ataUuid && (
          <Box sx={{ mt: 1 }}>
            <DocumentComponent uuid={step1Data.ataUuid} podeEnviar={false} />
          </Box>
        )}

        {step1Data.parceria === 'Sim' && (
          <>
            <DataConfirmation
              title='ATER - Atendidos por Instituições parceiras do Município'
              data={[
                {
                  label: 'Beneficiários atendidos pelas instituições parceiras',
                  value: step1Data.beneficiariosParceria,
                },
                {
                  label: 'Documento da parceria',
                  value: step1Data.docParceriaUuid ? 'Documento anexado' : 'Não anexado',
                },
                {
                  label: 'Instituições parceiras selecionadas',
                  value:
                    step1Data.instituicoes && step1Data.instituicoes.length > 0
                      ? step1Data.instituicoes
                        .map((inst) => {
                          if (inst === 'empaer') return 'EMPAER MT';
                          if (inst === 'ensino') return 'Instituições de Ensino';
                          if (inst === 'ongs') return 'ONGs';
                          if (inst === 'privadas') return step1Data.outraInstituicao || 'Instituição privada/outros';
                          return inst;
                        })
                        .join(', ')
                      : 'Nenhuma',
                },
              ]}
            />
            {step1Data.docParceriaUuid && (
              <Box sx={{ mt: 1 }}>
                <DocumentComponent uuid={step1Data.docParceriaUuid} podeEnviar={false} />
              </Box>
            )}

            <Box>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                Relatórios Técnicos Consolidados das Instituições Parceiras
              </Typography>
              <Stack direction="column" spacing={2}>
                {INSTITUICOES_PARCEIRAS.map(inst =>
                  step1Data.instituicoes.includes(inst.value) ? (
                    <Box key={inst.value}>
                      <Typography sx={{ fontWeight: 500, mb: 0.5 }}>
                        {inst.label}
                      </Typography>
                      <DocumentComponent
                        uuid={step1Data[`relatorioUuid_${inst.value}` as keyof Step1AnswersType] as string | null}
                        podeEnviar={false}
                      />
                    </Box>
                  ) : null
                )}
              </Stack>
            </Box>
          </>
        )}

        <Stack
          direction='row'
          spacing={2}
          justifyContent='center'
          alignItems='center'
          marginTop='2rem'
        >
          <Button variant='text' color='primary' onClick={onBack}>
            Voltar
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleConfirm}
          >
            Confirmar e salvar
          </Button>
        </Stack>
        <Stack />

        <AlertModal
          open={successOpen}
          title='Etapa Concluída com Sucesso'
          message='O preenchimento dos dados dessa etapa foi concluído com sucesso.'
          buttonLabel='Avançar'
          onClose={handleCloseSuccess}
        />
      </Stack>
    </>
  );
};

export default Step2;
